import { useEffect, useState } from 'react';
import { tagPickAnswers, revealMark } from '../lib/tagPick.js';
import './TagPickQuestion.css';

/**
 * The learner's side of a tag-pick question: every name as a name tag, all
 * on screen at once. Tapping a tag strikes it out ("Python refuses this
 * one"); tapping again takes it back. At reveal the tags show the answer
 * key — refused ones struck, each with ✓ / ✗ and why — the same as the
 * projector. The learner's own picks live only in this screen, and a reload
 * after the reveal would lose them; their own result is the banner above.
 */
export default function TagPickQuestion({ question, answered, onSubmit }) {
  const { payload, revealed } = question;
  const isLocked = answered || Boolean(revealed);
  const [struck, setStruck] = useState(() => new Set());

  useEffect(() => {
    setStruck(new Set());
  }, [question.id]);

  const toggle = (id) => {
    if (isLocked) return;
    setStruck((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="card stack tagpick">
      <h2 className="tagpick-title">{payload.instruction}</h2>
      {payload.hint && !isLocked && <p className="tagpick-hint">{payload.hint}</p>}

      <div className="tagpick-tags">
        {payload.items.map((item) => {
          const key = revealed && item.answer ? revealMark(item) : null;
          const isStruck = key ? key.struck : struck.has(item.id);
          return (
            <div key={item.id} className="tagpick-cell">
              <button
                type="button"
                className={`tagpick-tag ${isStruck ? 'is-struck' : ''}`}
                aria-pressed={isStruck}
                disabled={isLocked}
                onClick={() => toggle(item.id)}
              >
                <span>{item.caption}</span>
              </button>
              {key && (
                <span className={`tagpick-why ${key.struck ? 'is-wrong' : 'is-right'}`}>
                  <b aria-hidden="true">{key.mark}</b>
                  {key.reason}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {!isLocked && (
        <>
          <p className="tagpick-count">ขีดฆ่าแล้ว {struck.size} ป้าย</p>
          <button
            className="btn btn-primary btn-block"
            onClick={() => onSubmit({ answers: tagPickAnswers(payload.items, struck) })}
          >
            ส่งคำตอบ
          </button>
        </>
      )}

      {isLocked && !revealed && (
        <p className="text-dim text-center">
          {answered ? 'ส่งคำตอบแล้ว รอผู้สอนเฉลย...' : 'เฉลยคำตอบแล้ว — ปิดรับคำตอบข้อนี้'}
        </p>
      )}
    </div>
  );
}
